import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Radio, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLocalization } from '@/hooks/useLocalization';

const SOURCE_LABELS: Record<string, string> = {
  jira: 'Jira',
  azure_devops: 'Azure DevOps',
  servicenow: 'ServiceNow',
  sharepoint: 'SharePoint',
  microsoft_planner: 'Microsoft Planner',
  microsoft_project: 'Microsoft Project',
};

function humanizeSource(source: string): string {
  return SOURCE_LABELS[source] ?? source
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface SyncSourceLabelProps {
  /** One or more sync sources feeding this card (comma-joined if more than one). */
  source: string;
  syncedAt: string;
  /** Presence enables the delete affordance; called only after the confirm dialog is accepted. */
  onDelete?: () => void;
}

export function SyncSourceLabel({ source, syncedAt, onDelete }: SyncSourceLabelProps) {
  const { t } = useLocalization();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sourceLabel = source
    .split(',')
    .map((s) => humanizeSource(s.trim()))
    .join(', ');

  return (
    <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border/60 text-xs text-muted-foreground">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="gap-1 font-normal cursor-help underline decoration-dotted underline-offset-2">
              <Radio className="h-3 w-3" />
              
              {t('Synced from')} {sourceLabel}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">
            
            {t('Last synced')} {formatDistanceToNow(new Date(syncedAt), { addSuffix: true })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {onDelete && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmOpen(true);
            }}
            className="p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-colors"
            aria-label={t('Delete synced record')}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('Delete this synced record?')}</AlertDialogTitle>
                <AlertDialogDescription>
                  
                  {t('This permanently deletes this project and its synced tasks from "')}{sourceLabel}{t('". This does not affect the source system — it will reappear here on the next sync unless you also disconnect the integration.')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setConfirmOpen(false)}>{t('Cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    onDelete();
                    setConfirmOpen(false);
                  }}
                >
                  
                  {t('Delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
